import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import RichTextRenderer from "../components/RichTextRenderer";
import SEOHead from "../components/SEOHead";
import { getSeoPage } from "../utils/seoPages";
import { Copy, Handshake, TrendingUp, CheckCircle2, ExternalLink, Wallet, Landmark, Users, Save, Send } from "lucide-react";

function formatMoney(value = 0) {
  return `${Number(value || 0).toFixed(2)}€`;
}

function splitName(name = "") {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

export default function Affiliate() {
  const { user } = useAuth();
  const { settings: s } = useSettings();
  const [me, setMe] = useState(null);
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", payout_method: "iban", payout_details: "" });
  const [registered, setRegistered] = useState(null);
  const [copied, setCopied] = useState("");
  const [payoutForm, setPayoutForm] = useState({ payout_method: "iban", payout_details: "", account_holder: "" });
  const [requestAmount, setRequestAmount] = useState("");
  const [savingPayout, setSavingPayout] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const seo = getSeoPage(s, "/affiliate");

  const loadMe = async () => {
    if (!user) return;
    const { data } = await api.get("/affiliate/me");
    setMe(data);
    setPayoutForm({
      payout_method: data.affiliate?.payout_method || "iban",
      payout_details: data.affiliate?.payout_details || "",
      account_holder: data.affiliate?.account_holder || data.affiliate?.name || user.name || "",
    });
    if (!requestAmount) setRequestAmount(String(data.wallet?.available_eur || 0));
  };

  useEffect(() => {
    loadMe().catch(() => setMe(null));
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  const pendingAmount = useMemo(
    () => (me?.commissions || []).filter((c) => c.status === "pending").reduce((sum, c) => sum + Number(c.amount_eur || 0), 0),
    [me]
  );

  const copy = (txt, key) => {
    navigator.clipboard.writeText(txt);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/affiliate/register", registerForm);
    setRegistered(data);
  };

  const savePayout = async () => {
    setSavingPayout(true);
    setError("");
    setMessage("");
    try {
      const { data } = await api.patch("/affiliate/me/payout", payoutForm);
      setMe(data);
      setMessage("Coordonnées de paiement sauvegardées.");
    } catch (e) {
      setError(e.response?.data?.detail || "Impossible de sauvegarder tes coordonnées de paiement.");
    } finally {
      setSavingPayout(false);
    }
  };

  const createPayoutRequest = async () => {
    setRequestingPayout(true);
    setError("");
    setMessage("");
    try {
      const amount = Number(String(requestAmount).replace(",", "."));
      const { data } = await api.post("/affiliate/me/payout-requests", {
        amount_eur: amount,
        iban: payoutForm.payout_details,
        account_holder: payoutForm.account_holder,
      });
      await loadMe();
      setMessage(`Demande de virement créée pour ${formatMoney(data.request?.amount_eur)}.`);
    } catch (e) {
      setError(e.response?.data?.detail || "Impossible de créer la demande de virement.");
    } finally {
      setRequestingPayout(false);
    }
  };

  // Public flow (non-customer)
  if (!user) {
    return (
    <>
      <SEOHead title={seo.title} description={seo.description} url={typeof window !== "undefined" ? window.location.href : ""} type="website" />
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="affiliate-public-page">
        <div className="text-center mb-10">
          <Handshake className="w-14 h-14 text-brand-orange mx-auto mb-3" />
          <h1 className="font-heading text-4xl sm:text-5xl font-black">{s.affiliate_page_title || "Rejoins notre Programme Partenaires SF Learning"}</h1>
          <RichTextRenderer html={s.affiliate_page_subtitle || "Propulse l'ingénierie du leadership autour de toi. Recommande nos programmes de haute performance et génère 25% de commission sur chaque transformation."} className="text-gray-600 mt-3" />
          {s.affiliate_page_description && <RichTextRenderer html={s.affiliate_page_description} className="text-sm text-gray-500 mt-4" />}
        </div>
        {registered ? (
          <div className="card p-8 text-center space-y-5" data-testid="affiliate-registered">
            <CheckCircle2 className="w-12 h-12 text-brand-green mx-auto" />
            <h2 className="font-heading text-2xl font-bold">Bienvenue ! Voici ton code unique</h2>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
              <p className="label mb-2">Code</p>
              <p className="font-mono text-3xl font-black text-brand-green">{registered.code}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
              <p className="label mb-2">Lien à partager</p>
              <p className="font-mono text-sm break-all">{`${window.location.origin}/?ref=${registered.code}`}</p>
              <button onClick={() => copy(`${window.location.origin}/?ref=${registered.code}`, "url")} className="btn-ghost mt-3" data-testid="copy-affiliate-public-link">
                <Copy className="w-4 h-4" /> {copied === "url" ? "Copié !" : "Copier le lien"}
              </button>
            </div>
            <Link to="/login" className="btn-primary" data-testid="affiliate-login-cta">
              {s.affiliate_page_cta || "Accéder à mon dashboard"} <ExternalLink className="w-4 h-4" />
            </Link>
            <p className="text-xs text-gray-500">Astuce : crée un compte pour suivre tes statistiques en détail.</p>
          </div>
        ) : (
          <form onSubmit={handleRegister} className="card p-6 space-y-4" data-testid="affiliate-register-form">
            <h2 className="font-heading text-xl font-bold">Activation de ton compte Partenaire — Accès Immédiat</h2>
            <div>
              <label className="label mb-1 block">Prénom & Nom</label>
              <input value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })} required className="input" data-testid="affiliate-name-input" placeholder="Marie Dupont" />
            </div>
            <div>
              <label className="label mb-1 block">Email</label>
              <input type="email" value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} required className="input" data-testid="affiliate-email-input" placeholder="ton@email.com" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label mb-1 block">Méthode de paiement</label>
                <select value={registerForm.payout_method} onChange={(e) => setRegisterForm({ ...registerForm, payout_method: e.target.value })} className="input" data-testid="affiliate-payout-method-select">
                  <option value="iban">Virement bancaire (IBAN)</option>
                  <option value="paypal">PayPal</option>
                  <option value="crypto">Crypto wallet</option>
                </select>
              </div>
              <div>
                <label className="label mb-1 block">Coordonnée</label>
                <input value={registerForm.payout_details} onChange={(e) => setRegisterForm({ ...registerForm, payout_details: e.target.value })} className="input" placeholder="IBAN, email PayPal, adresse..." data-testid="affiliate-payout-details-input" />
              </div>
            </div>
            <button type="submit" className="btn-primary w-full justify-center" data-testid="affiliate-register-submit">
              {s.affiliate_page_form_cta || "Obtenir mes accès Partenaires 🤝"} <Handshake className="w-4 h-4" />
            </button>
          </form>
        )}
      </div>
    </>
    );
  }

  // Logged-in dashboard
  if (!me) return <div className="min-h-[60vh]" />;
  const url = `${window.location.origin}/?ref=${me.affiliate.code}`;

  return (
    <>
      <SEOHead title={seo.title} description={seo.description} url={typeof window !== "undefined" ? window.location.href : ""} type="website" />
      <div className="max-w-6xl mx-auto px-4 py-12" data-testid="affiliate-dashboard-page">
      <p className="label">Espace parrainage</p>
      <h1 className="font-heading text-4xl font-black mt-1 mb-2">Programme d'apport d'affaires</h1>
      <p className="text-gray-600 mb-8">Partage ton lien, recommande, et touche tes commissions.</p>

      {message && <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</div>}
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="card p-5"><p className="label">Clics</p><p className="font-heading text-3xl font-black mt-1">{me.affiliate.clicks}</p></div>
        <div className="card p-5"><p className="label">Conversions</p><p className="font-heading text-3xl font-black mt-1">{me.affiliate.conversions}</p></div>
        <div className="card p-5"><p className="label">Total gagné</p><p className="font-heading text-3xl font-black mt-1 text-brand-green">{formatMoney(me.wallet?.earned_eur ?? me.affiliate.total_earned_eur)}</p></div>
        <div className="card p-5"><p className="label">Solde dispo</p><p className="font-heading text-3xl font-black mt-1 text-brand-green">{formatMoney(me.wallet?.available_eur)}</p></div>
        <div className="card p-5"><p className="label">En attente</p><p className="font-heading text-3xl font-black mt-1 text-brand-orange">{formatMoney(pendingAmount)}</p></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="card p-6">
          <p className="label mb-2">Ton code</p>
          <p className="font-mono text-3xl font-black text-brand-green">{me.affiliate.code}</p>
          <button onClick={() => copy(me.affiliate.code, "code")} className="btn-ghost mt-3" data-testid="copy-affiliate-code-btn">
            <Copy className="w-4 h-4" /> {copied === "code" ? "Copié !" : "Copier"}
          </button>
        </div>
        <div className="card p-6">
          <p className="label mb-2">Ton lien personnalisé</p>
          <p className="font-mono text-sm break-all bg-emerald-50 p-3 rounded">{url}</p>
          <button onClick={() => copy(url, "url")} className="btn-ghost mt-3" data-testid="copy-affiliate-url-btn">
            <Copy className="w-4 h-4" /> {copied === "url" ? "Copié !" : "Copier le lien"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="card p-6">
          <h2 className="font-heading text-xl font-bold mb-3 flex items-center gap-2"><Wallet className="w-5 h-5 text-brand-green" /> Portefeuille virtuel</h2>
          <p className="text-sm text-gray-600 mb-4">Tu peux utiliser ton solde disponible pour acheter une formation directement au checkout.</p>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="rounded-xl bg-emerald-50 p-3"><p className="label">Disponible</p><p className="font-bold text-brand-green">{formatMoney(me.wallet?.available_eur)}</p></div>
            <div className="rounded-xl bg-gray-50 p-3"><p className="label">Utilisé</p><p className="font-bold">{formatMoney(me.wallet?.spent_eur)}</p></div>
            <div className="rounded-xl bg-amber-50 p-3"><p className="label">Réservé</p><p className="font-bold text-brand-orange">{formatMoney(me.wallet?.payout_reserved_eur)}</p></div>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="font-heading text-xl font-bold mb-3 flex items-center gap-2"><Landmark className="w-5 h-5 text-brand-green" /> Virement bancaire</h2>
          <div className="space-y-3">
            <div>
              <label className="label mb-1 block">Titulaire du compte</label>
              <input className="input" value={payoutForm.account_holder} onChange={(e) => setPayoutForm({ ...payoutForm, account_holder: e.target.value })} placeholder="Prénom Nom" />
            </div>
            <div>
              <label className="label mb-1 block">IBAN</label>
              <input className="input uppercase" value={payoutForm.payout_details} onChange={(e) => setPayoutForm({ ...payoutForm, payout_method: "iban", payout_details: e.target.value.toUpperCase() })} placeholder="FR76..." />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
              <button type="button" onClick={savePayout} disabled={savingPayout} className="btn-ghost justify-center"><Save className="w-4 h-4" /> {savingPayout ? "Sauvegarde..." : "Sauvegarder l’IBAN"}</button>
              <div className="flex gap-2">
                <input className="input w-32" value={requestAmount} onChange={(e) => setRequestAmount(e.target.value)} placeholder="Montant" />
                <button type="button" onClick={createPayoutRequest} disabled={requestingPayout} className="btn-primary"><Send className="w-4 h-4" /> Demander</button>
              </div>
            </div>
            <p className="text-xs text-gray-500">La demande de virement est enregistrée pour traitement manuel par l’admin. Aucun virement bancaire automatique n’est déclenché sans prestataire bancaire connecté.</p>
          </div>
        </div>
      </div>

      <h2 className="font-heading text-xl font-bold mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-brand-green" /> Mes filleuls</h2>
      {(!me.referrals || me.referrals.length === 0) ? (
        <div className="card p-8 text-center text-gray-500 mb-8">Aucun filleul pour le moment.</div>
      ) : (
        <>
        {/* Mobile : une fiche par filleul (lecture de haut en bas). */}
        <div className="space-y-3 mb-8 lg:hidden">
          {me.referrals.map((ref) => {
            const fallback = splitName(ref.name);
            return (
              <div key={`m-${ref.referral_event_id || ref.referred_user_id}`} className="card p-4">
                <p className="font-heading font-bold break-words">{[ref.first_name || fallback.firstName, ref.last_name || fallback.lastName].filter(Boolean).join(" ") || "—"}</p>
                <p className="break-all text-sm text-gray-600">{ref.email || "—"}</p>
                <div className="mt-3 border-t border-emerald-100 pt-3">
                  {ref.has_purchased ? (
                    <div className="flex flex-wrap gap-1">
                      {ref.courses.map((course) => <span key={course.course_id} className="badge">{course.title}</span>)}
                    </div>
                  ) : <span className="badge orange">Pas encore d’achat</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="card overflow-hidden mb-8 hidden lg:block">
          <table className="w-full text-sm">
            <thead className="bg-emerald-50">
              <tr><th className="text-left p-3">Prénom</th><th className="text-left p-3">Nom</th><th className="text-left p-3">Email</th><th className="text-left p-3">Formation achetée</th></tr>
            </thead>
            <tbody>
              {me.referrals.map((ref) => {
                const fallback = splitName(ref.name);
                return (
                  <tr key={ref.referral_event_id || ref.referred_user_id} className="border-t border-emerald-100">
                    <td className="p-3">{ref.first_name || fallback.firstName || "—"}</td>
                    <td className="p-3">{ref.last_name || fallback.lastName || "—"}</td>
                    <td className="p-3 text-gray-600">{ref.email || "—"}</td>
                    <td className="p-3">
                      {ref.has_purchased ? (
                        <div className="space-y-1">
                          {ref.courses.map((course) => <span key={course.course_id} className="badge mr-1 mb-1">{course.title}</span>)}
                        </div>
                      ) : <span className="badge orange">Pas encore d’achat</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      <h2 className="font-heading text-xl font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-brand-green" /> Mes commissions</h2>
      {me.commissions.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">Aucune commission pour le moment. Partage ton lien pour commencer !</div>
      ) : (
        <>
        {/* Mobile : une fiche par commission. */}
        <div className="space-y-3 lg:hidden">
          {me.commissions.map((c) => (
            <div key={`m-${c.commission_id}`} className="card p-4">
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 font-heading font-bold break-words">{c.course_title}</p>
                <p className="shrink-0 font-bold">{formatMoney(c.amount_eur)}</p>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 border-t border-emerald-100 pt-3">
                <span className="text-sm text-gray-500">{new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                {c.status === "paid" ? <span className="badge"><CheckCircle2 className="w-3 h-3" /> Payé</span> : <span className="badge orange">En attente</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="card overflow-hidden hidden lg:block">
          <table className="w-full text-sm">
            <thead className="bg-emerald-50">
              <tr><th className="text-left p-3">Formation</th><th className="text-left p-3">Date</th><th className="text-right p-3">Montant</th><th className="text-right p-3">Statut</th></tr>
            </thead>
            <tbody>
              {me.commissions.map((c) => (
                <tr key={c.commission_id} className="border-t border-emerald-100" data-testid={`commission-${c.commission_id}`}>
                  <td className="p-3">{c.course_title}</td>
                  <td className="p-3 text-gray-500">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="p-3 text-right font-bold">{formatMoney(c.amount_eur)}</td>
                  <td className="p-3 text-right">{c.status === "paid" ? <span className="badge"><CheckCircle2 className="w-3 h-3" /> Payé</span> : <span className="badge orange">En attente</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      </div>
    </>
  );
}
